import Link from 'next/link';
import type { JSX } from 'react';
import { coverBackground } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';

/**
 * Three editorial registers for article cards. Used by the
 * {@code BlogArticleGrid} block and by
 * any host {@code BlogDetailLayout} that wants to render related
 * articles. The active variant is driven by the host's
 * {@code BlogModuleConfig.articleCardVariant}.
 *
 * <ul>
 *   <li>{@code image-led}: cover dominates, title + excerpt below.
 *       Default — works for hosts with strong photography.</li>
 *   <li>{@code minimal}: text-only, hairline rule between cards.
 *       Reading-list feel — works for hosts without consistent
 *       covers.</li>
 *   <li>{@code magazine}: cover + serif headline + dek + byline.
 *       Editorial register — works for hosts on long-form content.</li>
 * </ul>
 */
export type ArticleCardVariant = 'image-led' | 'minimal' | 'magazine';

export interface ArticleCardProps {
  href: string;
  title: string;
  excerpt?: string;
  date?: string;
  author?: string;
  category?: string;
  readTime?: string;
  cover?: string;
}

export function resolveArticleCard(
  variant: ArticleCardVariant | undefined,
): (props: ArticleCardProps) => JSX.Element {
  if (variant === 'minimal') return ArticleCardMinimal;
  if (variant === 'magazine') return ArticleCardMagazine;
  return ArticleCardImageLed;
}

export function ArticleCardImageLed({
  href,
  title,
  excerpt,
  date,
  author,
  category,
  readTime,
  cover,
}: ArticleCardProps) {
  return (
    <Card asChild>
      <Link
        href={href}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: 'column',
          textDecoration: 'none',
          color: 'inherit',
          background: 'var(--color-surface-card, #fff)',
          border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
          borderRadius: 'var(--radius-md, 8px)',
          overflow: 'hidden',
          transition: 'transform 120ms ease, box-shadow 120ms ease',
        }}
        className="lms-article-card lms-article-card--image-led"
      >
        <div
          aria-hidden
          style={{
            aspectRatio: '16 / 10',
            ...coverBackground(cover),
          }}
        />
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {category && <CategoryPill text={category} />}
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-md, 16px)',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {title}
          </h3>
          {excerpt && (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm, 14px)',
                lineHeight: 1.5,
                color: 'var(--color-text-secondary, #71717a)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {excerpt}
            </p>
          )}
          <MetaLine author={author} date={date} readTime={readTime} />
        </div>
      </Link>
    </Card>
  );
}

export function ArticleCardMinimal({
  href,
  title,
  excerpt,
  date,
  author,
  category,
  readTime,
}: ArticleCardProps) {
  return (
    <Card asChild>
      <Link
        href={href}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: 'column',
          textDecoration: 'none',
          color: 'inherit',
          gap: 6,
          padding: 'var(--space-5, 20px) 0',
          borderTop: '1px solid var(--color-border, rgba(0,0,0,0.08))',
        }}
        className="lms-article-card lms-article-card--minimal"
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {category && <CategoryPill text={category} />}
          {date && (
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {date}
            </span>
          )}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg, 18px)',
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: 'var(--font-tracking-snug, -0.005em)',
          }}
        >
          {title}
        </h3>
        {excerpt && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 1.55,
              color: 'var(--color-text-secondary, #71717a)',
              maxWidth: 640,
            }}
          >
            {excerpt}
          </p>
        )}
        {(author || readTime) && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-secondary, #71717a)',
              marginTop: 2,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            {author && <span>{author}</span>}
            {author && readTime && (
              <span aria-hidden style={{ opacity: 0.5 }}>
                ·
              </span>
            )}
            {readTime && <span>{readTime}</span>}
          </div>
        )}
      </Link>
    </Card>
  );
}

export function ArticleCardMagazine({
  href,
  title,
  excerpt,
  author,
  category,
  readTime,
  cover,
}: ArticleCardProps) {
  return (
    <Card asChild>
      <Link
        href={href}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: 'column',
          textDecoration: 'none',
          color: 'inherit',
          gap: 12,
        }}
        className="lms-article-card lms-article-card--magazine"
      >
        <div
          aria-hidden
          style={{
            aspectRatio: '4 / 3',
            ...coverBackground(cover),
            borderRadius: 'var(--radius-sm, 4px)',
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {category && <CategoryRule text={category} />}
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xl, 22px)',
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: 'var(--font-tracking-snug, -0.012em)',
              fontFamily: 'var(--font-family-serif, "Source Serif Pro", Georgia, serif)',
            }}
          >
            {title}
          </h3>
          {excerpt && (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm, 14px)',
                lineHeight: 1.6,
                color: 'var(--color-text-secondary, #71717a)',
                fontStyle: 'italic',
              }}
            >
              {excerpt}
            </p>
          )}
          {author && (
            <div
              style={{
                fontSize: '11px',
                letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                textTransform: 'uppercase',
                color: 'var(--color-text-secondary, #71717a)',
                fontWeight: 600,
                marginTop: 4,
              }}
            >
              By {author}
              {readTime && ` · ${readTime}`}
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
}

function CategoryPill({ text }: { text: string }) {
  return (
    <span
      style={{
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: 'var(--font-tracking-wider, 0.04em)',
        textTransform: 'uppercase',
        color: 'var(--color-brand-600, #4f46e5)',
      }}
    >
      {text}
    </span>
  );
}

function CategoryRule({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 700,
        letterSpacing: 'var(--font-tracking-widest, 0.16em)',
        textTransform: 'uppercase',
        color: 'var(--color-brand-600, #4f46e5)',
        borderTop: '1px solid currentColor',
        borderBottom: '1px solid currentColor',
        padding: 'var(--space-1, 4px) 0',
        width: 'fit-content',
      }}
    >
      {text}
    </span>
  );
}

function MetaLine({
  author,
  date,
  readTime,
}: {
  author?: string;
  date?: string;
  readTime?: string;
}) {
  const parts: string[] = [];
  if (author) parts.push(author);
  if (date) parts.push(date);
  if (readTime) parts.push(readTime);
  if (parts.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 4,
        fontSize: 'var(--font-size-xs, 12px)',
        color: 'var(--color-text-secondary, #71717a)',
      }}
    >
      {parts.join(' · ')}
    </div>
  );
}
